import { useEffect, useRef, useState } from 'react';

import { Form, withFormik } from 'formik';
import { connect } from 'react-redux';
import { compose } from 'recompact';
import { css } from 'styled-components';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import CapsuleIcon from 'src/app/components/Elements/CapsuleIcon';
import { FormikPhoneField } from 'src/app/components/Form/FormikPhoneField/FormikPhoneField';
import { FormikTextField } from 'src/app/components/Form/FormikTextField/FormikTextField';
import { useFocusOnFirstFormError } from 'src/app/components/Form/Hooks/useFocusOnFirstFormError';
import LearnMoreModal from 'src/app/components/Modal/Onboarding/LearnMore/LearnMoreModal';
import { Step } from 'src/app/components/Pages/GetStarted/StepLayout/Step';
import { onboardingAccountNotifyTransfer, segmentTrack, setOnboardingFormValues } from 'src/app/state/actions';
import type { Dispatch, RootState } from 'src/app/state/store';
import { CAPSULE_ICON_STRINGS } from 'src/consts';

import { ButtonContainer, StyledButton } from '../../styles';

import { validation } from './validation';

const UnconnectedDoctorStep = ({
  errors,
  handleSubmit,
  isSubmitting,
  isValid,
  isValidating,
  onSetOnboardingFormValues,
  segmentTrack,
  values,
}) => {
  const formRef = useRef(null);
  const [dispatchedEvent, setDispatchedEvent] = useState(false);
  const [showLearnMoreModal, setShowLearnMoreModal] = useState(false);

  const handleCloseModal = () => {
    setShowLearnMoreModal(false);
  };

  useFocusOnFirstFormError(formRef, errors, isSubmitting, isValidating, false);

  useEffect(() => {
    if (!dispatchedEvent) {
      segmentTrack(UNIFIED.viewOnboardingPharmacyInfo, { form_type: 'doctor form' });
      segmentTrack(UNIFIED.viewOnboardingDoctorInfo, {});

      setDispatchedEvent(true);
    }
  }, [dispatchedEvent, segmentTrack]);

  return (
    <Step
      title="Which doctor should we get your prescriptions from?"
      text="We’ll contact them on your behalf"
      customHandlePrevText={'Back'}
    >
      <Form ref={formRef}>
        <FormikTextField
          adornment={{
            left: (
              <CapsuleIcon
                name="stethoscope"
                iconString={CAPSULE_ICON_STRINGS.stethoscope_icon}
                css={css`
                  padding: 0 !important;
                  path {
                    fill: ${({ theme }) => theme.colors.capsuleGreen60};
                  }
                  svg {
                    width: 100% !important;
                    height: 100% !important;
                  }
                `}
              />
            ),
          }}
          name="doctorName"
          type="text"
          label={{
            content: 'Doctor or Practice Name',
            isHidden: false,
          }}
          onBlur={() => {
            onSetOnboardingFormValues(values);
          }}
        />
        <FormikPhoneField
          adornment={{
            left: (
              <CapsuleIcon
                name="phone"
                iconString={CAPSULE_ICON_STRINGS.phone}
                css={css`
                  padding: ${({ theme }) => theme.space.s1} !important;
                  path {
                    fill: ${({ theme }) => theme.colors.capsuleGreen60};
                  }
                  svg {
                    width: 100% !important;
                    height: 100% !important;
                  }
                `}
              />
            ),
          }}
          autoComplete="tel"
          name="doctorPhone"
          label={{ isHidden: false, content: `Doctor’s phone number` }}
          onBlur={() => {
            onSetOnboardingFormValues(values);
          }}
        />
        <ButtonContainer>
          <StyledButton size="medium" onClick={handleSubmit} disabled={!isValid} type="submit" style={'capsuleBlue50'}>
            Continue
          </StyledButton>
        </ButtonContainer>
      </Form>
      <LearnMoreModal
        showModal={showLearnMoreModal}
        handleCloseModal={handleCloseModal}
        title={'New prescriptions'}
        body={`Any doctor can send a prescription to Capsule. We’ll contact your doctor for you and ask them
          for your prescriptions. It may take 1 business day to receive your prescriptions. Once your prescription
          is ready, we’ll send you a text message so you can schedule your free delivery.`}
      />
    </Step>
  );
};

const mapStateToProps = (state: RootState) => ({
  onboardingForm: state.onboardingForm,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onSetOnboardingFormValues: values => dispatch(setOnboardingFormValues(values)),
  segmentTrack: (event: UnifiedEvent, payload) => dispatch(segmentTrack(event, payload)),
  onNotificationTransfer: (accountId: string) => dispatch(onboardingAccountNotifyTransfer(accountId)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

const enhanceWithFormik = withFormik({
  mapPropsToValues: props => props.onboardingForm,
  validationSchema: validation.schema,
  validateOnChange: true,
  validateOnBlur: true,
  validateOnMount: true,
  handleSubmit: async (values, { props }) => {
    const { onSetOnboardingFormValues, handleNextClick } = props;
    onSetOnboardingFormValues(values);
    handleNextClick();
  },
});

export const DoctorStep = compose(connector, enhanceWithFormik)(UnconnectedDoctorStep);
